import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone } from "lucide-react";

export const TapeMakingMachine = () => {
  return (
    <>
      <Helmet>
        <title>Tape Making Machine Supplier in India - Hero Impex | Complete Tape Production Lines</title>
        <meta name="description" content="Trusted tape making machine supplier in India. Complete BOPP tape production lines, adhesive coating, slitting & rewinding. Turnkey solutions. 45+ years. Get quote!" />
        <meta name="keywords" content="tape making machine supplier India, BOPP tape production line, adhesive tape manufacturing machine, complete tape plant, tape making equipment" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-amber-500">Complete Tape Manufacturing Solutions</Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Tape Making Machine Supplier in India
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Your one-stop supplier for complete tape manufacturing equipment. From coating to slitting to packaging - we provide turnkey tape production lines for BOPP, masking, double-sided, and specialty tapes.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8">
                    Get Complete Plant Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+919884012741">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                    <Phone className="w-5 h-5 mr-2" />
                    Talk to Expert: +91-9884012741
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">India's Most Trusted Tape Making Machine Supplier</h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Hero Impex India Private Limited is a leading supplier of tape making machinery with 45+ years of industry experience. We supply complete tape manufacturing plants to entrepreneurs, established manufacturers, and export houses across India and globally.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Whether you're starting a new tape business or expanding your existing production, we provide end-to-end solutions including machinery supply, installation, training, and ongoing technical support.
              </p>
            </div>

            {/* Complete Plant Components */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Complete Tape Manufacturing Plant Components</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="border-2 border-amber-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">1</span>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">Coating Machine</h3>
                    <p className="text-sm text-slate-600">
                      Hot melt, solvent, or water-based adhesive coating system
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">2</span>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">Slitting Machine</h3>
                    <p className="text-sm text-slate-600">
                      Precision slitting for converting jumbo rolls to desired widths
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-green-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">3</span>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">Rewinding Machine</h3>
                    <p className="text-sm text-slate-600">
                      Core winding and log formation for final packaging
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-purple-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">4</span>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">Ancillary Equipment</h3>
                    <p className="text-sm text-slate-600">
                      Printing units, label dispensers, packaging machines
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Why Choose Hero Impex */}
            <div className="mb-12 bg-amber-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Hero Impex as Your Tape Machine Supplier?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">One-Stop Solution Provider</h4>
                    <p className="text-slate-600">Single vendor for all machinery - coating, slitting, printing, packaging</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Turnkey Project Management</h4>
                    <p className="text-slate-600">From layout design to commissioning - complete project execution</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Technical Training Included</h4>
                    <p className="text-slate-600">Comprehensive operator and maintenance training at your facility</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Raw Material Sourcing Support</h4>
                    <p className="text-slate-600">Guidance on BOPP film, adhesive, and core suppliers</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Fast Delivery & Installation</h4>
                    <p className="text-slate-600">Machines delivered and commissioned within 90-120 days</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Spare Parts Availability</h4>
                    <p className="text-slate-600">Critical spares in stock with 24-hour delivery across India</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Types of Tapes */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Types of Tapes You Can Manufacture</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Packaging Tapes</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• BOPP Brown Tape</li>
                      <li>• Transparent Tape</li>
                      <li>• Printed Packing Tape</li>
                      <li>• Colored BOPP Tape</li>
                      <li>• Heavy-Duty Carton Tape</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Specialty Tapes</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• Masking Tape</li>
                      <li>• Double-Sided Tape</li>
                      <li>• Foam Tape</li>
                      <li>• Duct Tape</li>
                      <li>• Surface Protection Tape</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Industrial Tapes</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• Electrical Insulation Tape</li>
                      <li>• Medical Adhesive Tape</li>
                      <li>• Automotive Tapes</li>
                      <li>• Filament Tape</li>
                      <li>• Transfer Adhesive Tape</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Investment & ROI */}
            <div className="mb-12 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Start Your Tape Business - Investment & ROI Guide</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Small-Scale Setup</h3>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>Capacity:</strong> 500-1000 rolls/day
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>Machine Width:</strong> 500-1000mm
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>Space Required:</strong> 2000-3000 sq.ft
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>ROI Period:</strong> 18-24 months
                      </div>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Medium-Scale Setup</h3>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>Capacity:</strong> 2000-4000 rolls/day
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>Machine Width:</strong> 1000-1600mm
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>Space Required:</strong> 4000-6000 sq.ft
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <strong>ROI Period:</strong> 12-18 months
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 bg-white rounded-lg p-6 border-l-4 border-amber-500">
                <p className="text-slate-700">
                  <strong>💡 Business Tip:</strong> BOPP tape demand in India is growing at 12-15% annually driven by e-commerce and logistics. With the right machinery and marketing, tape manufacturing is a highly profitable business with consistent demand.
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-10 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Start Your Tape Manufacturing Business?</h2>
              <p className="text-xl mb-6 text-amber-50">
                Schedule a consultation with our business development team. We'll help you plan your complete tape manufacturing setup with machinery selection, layout design, and business guidance.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 text-lg px-8">
                    Get Complete Plant Quotation
                  </Button>
                </Link>
                <a href="https://wa.me/919884012741?text=I want to start a tape manufacturing business. Please send me plant details and quotation." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                    WhatsApp Business Enquiry
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-sm text-amber-100">
                ✓ Turnkey Solutions • ✓ Business Guidance • ✓ Training Included • ✓ Trusted by 800+ Manufacturers
              </p>
            </div>

          </div>
        </section>
      </div>
    </>
  );
};